import { useCallback, useEffect, useRef } from 'react';
import { FieldErrors, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { debounce } from 'lodash';

// Matches the default delay used by the useDebouncedState hook elsewhere in the app.
const AUTOSAVE_DEBOUNCE_MS = 500;

type UseFormAutosaveParams<T extends FieldValues> = {
  form: UseFormReturn<T>;
  enabled: boolean;
  onAutosave?: (values: Partial<T>) => void | Promise<void>;
  // Fields (typically free-text inputs) that are saved when focus leaves them
  // (see the returned onBlur) instead of on a debounce while the user types.
  saveOnBlurFields?: Path<T>[];
};

// Auto-saves any valid, user-driven field change via onAutosave: debounced by
// default, or on blur for the fields listed in saveOnBlurFields.
export const useFormAutosave = <T extends FieldValues>({
  form,
  enabled,
  onAutosave,
  saveOnBlurFields,
}: UseFormAutosaveParams<T>) => {
  // Read via a ref inside the debounced callback so the effect below doesn't need
  // onAutosave to be referentially stable across renders: recreating the effect
  // (e.g. because a parent re-render produced a new onAutosave closure)
  // would cancel any pending debounced timer before it had a chance to fire.
  const onAutosaveRef = useRef(onAutosave);
  onAutosaveRef.current = onAutosave;

  // Same reasoning as onAutosaveRef: callers may pass an inline array literal.
  const saveOnBlurFieldsRef = useRef(saveOnBlurFields);
  saveOnBlurFieldsRef.current = saveOnBlurFields;

  const flushPendingBlurFieldsRef = useRef<() => void>();
  // Saves that have started but not settled yet (including their onCompleted, which
  // is what marks the saved fields clean again).
  const inFlightSavesRef = useRef(new Set<Promise<void>>());

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const inFlightSaves = inFlightSavesRef.current;

    const saveField = async (name: string) => {
      const save = (async () => {
        await form.trigger(name as Path<T>);

        const error = (form.formState.errors as FieldErrors)[name];

        if (error) {
          return;
        }

        const value = form.getValues(name as Path<T>);
        await onAutosaveRef.current?.({ [name]: value } as Partial<T>);
      })();

      inFlightSaves.add(save);

      try {
        await save;
      } finally {
        inFlightSaves.delete(save);
      }
    };

    // Each field gets its own independent debounce/mutation, instead of one shared
    // timer for the whole form. This matters when a field fails to save for a reason
    // that isn't a validation error (e.g. a server-side "possible duplicate" check):
    // editing an unrelated field must not re-trigger that failed field's save (and
    // whatever side effect its error causes, like reopening a confirmation modal).
    const fieldDebouncers = new Map<string, ReturnType<typeof debounce>>();

    const getOrCreateDebouncer = (name: string) => {
      let debounced = fieldDebouncers.get(name);

      if (!debounced) {
        debounced = debounce(() => saveField(name), AUTOSAVE_DEBOUNCE_MS);
        fieldDebouncers.set(name, debounced);
      }

      return debounced;
    };

    // Blur fields changed since the last blur. Any blur flushes all of them, so we
    // don't need to map the blurred DOM element back to a field name; in practice
    // only the field that was just edited is pending.
    const pendingBlurFields = new Set<string>();

    const flushPendingBlurFields = () => {
      const names = Array.from(pendingBlurFields);
      pendingBlurFields.clear();
      names.forEach((name) => void saveField(name));
    };

    flushPendingBlurFieldsRef.current = flushPendingBlurFields;

    const subscription = form.watch((_values, { name, type }) => {
      // Only react to genuine user-driven changes, not programmatic form.reset() calls.
      if (type !== 'change' || !name) {
        return;
      }

      if (saveOnBlurFieldsRef.current?.includes(name)) {
        pendingBlurFields.add(name);
        return;
      }

      void getOrCreateDebouncer(name)();
    });

    return () => {
      subscription.unsubscribe();
      // Don't lose an edit whose field never blurred (e.g. the form was closed).
      flushPendingBlurFields();
      flushPendingBlurFieldsRef.current = undefined;
      fieldDebouncers.forEach((debounced) => debounced.cancel());
      fieldDebouncers.clear();
    };
  }, [enabled, form]);

  const onBlur = useCallback(() => {
    flushPendingBlurFieldsRef.current?.();
  }, []);

  // Saves any pending blur fields and waits for every in-flight save to settle, so
  // callers (e.g. closing the form) see the form's up-to-date dirty state afterwards.
  const settle = useCallback(async () => {
    flushPendingBlurFieldsRef.current?.();
    await Promise.allSettled(Array.from(inFlightSavesRef.current));
  }, []);

  return { onBlur, settle };
};
